import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Card,
  Row,
  Col,
  Dropdown,
  Modal,
  Typography,
  Progress,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  CopyOutlined,
  StopOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { loadAllCustomer } from "../../redux/actions/customer/getCustomerAction";
import { getAllOrders, cancelOrder } from "../../api/orderApi";
import OrderStatusBadge from "./OrderStatusBadge";
import PriorityBadge from "./PriorityBadge";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;
const { confirm } = Modal;
const DEFAULT_ORDER_STATUSES = ["confirmed", "planned", "in_production"];

const OrderList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customers = useSelector((state) => state.customers?.list || []);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: DEFAULT_ORDER_STATUSES,
    priority: undefined,
    customer_id: undefined,
    search: "",
    start_date: undefined,
    end_date: undefined,
  });

  // Fetch orders
  const fetchOrders = useCallback(
    async (page = 1, pageSize = 50) => {
      setLoading(true);
      try {
        const params = {
          page,
          count: pageSize,
        };

        if (filters.status?.length) params.status = filters.status.join(",");
        if (filters.priority) params.priority = filters.priority;
        if (filters.customer_id) params.customer_id = filters.customer_id;
        if (filters.search) params.search = filters.search;
        if (filters.start_date) params.start_date = filters.start_date;
        if (filters.end_date) params.end_date = filters.end_date;

        const response = await getAllOrders(params);
        setOrders(response.data || []);
        setPagination({
          current: response.page || page,
          pageSize: response.limit || pageSize,
          total: response.total || 0,
        });
      } catch (error) {
        console.log(error.message);
        toast.error("Failed to fetch orders");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    dispatch(loadAllCustomer({ status: true, page: 1, limit: 100 }));
  }, [dispatch]);

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    fetchOrders(paginationInfo.current, paginationInfo.pageSize);
  };

  // Handle cancel order
  const handleCancelOrder = (order) => {
    confirm({
      title: "Cancel Order",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to cancel order ${order.order_number}?`,
      okText: "Yes, Cancel It",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await cancelOrder(order.id, "Cancelled by user");
          toast.success("Order cancelled successfully");
          fetchOrders(pagination.current, pagination.pageSize);
        } catch (error) {
          toast.error(
            error.response?.data?.message || "Failed to cancel order"
          );
        }
      },
    });
  };

  // Handle date range change
  const handleDateChange = (dates) => {
    if (dates) {
      setFilters({
        ...filters,
        start_date: dates[0].format("YYYY-MM-DD"),
        end_date: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      setFilters({
        ...filters,
        start_date: undefined,
        end_date: undefined,
      });
    }
  };

  // Action menu per row
  const getActionMenuItems = (record) => ({
    items: [
      {
        key: "view",
        icon: <EyeOutlined />,
        label: "View Details",
        onClick: () => navigate(`/order/${record.id}`),
      },
      {
        key: "edit",
        icon: <EditOutlined />,
        label: "Edit",
        onClick: () => navigate(`/order/${record.id}/edit`),
        disabled: ["delivered", "cancelled"].includes(record.status),
      },
      {
        key: "clone",
        icon: <CopyOutlined />,
        label: "Clone Order",
        onClick: () => navigate(`/order/new?cloneFrom=${record.id}`),
      },
      { type: "divider" },
      {
        key: "cancel",
        icon: <StopOutlined />,
        label: "Cancel Order",
        danger: true,
        onClick: () => handleCancelOrder(record),
        disabled: ["delivered", "cancelled"].includes(record.status),
      },
    ],
  });

  // Table columns
  const columns = [
    {
      title: "Order #",
      dataIndex: "order_number",
      key: "order_number",
      sorter: (a, b) => (a.order_number || "").localeCompare(b.order_number || ""),
      render: (text, record) => (
        <Link to={`/order/${record.id}`}>
          <strong>{text}</strong>
        </Link>
      ),
    },
    {
      title: "PO #",
      dataIndex: "customer_po_number",
      key: "customer_po_number",
      width: 90,
      sorter: (a, b) => (Number(a.customer_po_number) || 0) - (Number(b.customer_po_number) || 0),
      render: (v) => v || "-",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 110,
      sorter: (a, b) => {
        const order = { urgent: 4, high: 3, normal: 2, low: 1 };
        return (order[a.priority] || 0) - (order[b.priority] || 0);
      },
      render: (priority) => <PriorityBadge priority={priority} />,
    },
    {
      title: "Customer",
      key: "customer",
      sorter: (a, b) => ((a.customer_name || a.customer?.name || "").localeCompare(b.customer_name || b.customer?.name || "")),
      render: (_, record) =>
        record.customer_name || record.customer?.name || record.customer_id || "-",
    },
    {
      title: "Items",
      dataIndex: "items",
      key: "items_count",
      render: (items) => {
        const count = items?.length || 0;
        const totalUnits = items?.reduce(
          (sum, item) => sum + (Number(item.quantity) || 0),
          0
        );
        return (
          <Space direction="vertical" size={0}>
            <Text>{count} items</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {totalUnits?.toLocaleString("en-IN")} units
            </Text>
          </Space>
        );
      },
      align: "center",
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      sorter: (a, b) => (Number(a.total_amount) || 0) - (Number(b.total_amount) || 0),
      render: (amount) =>
        `₹${Number(amount || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
        })}`,
      align: "right",
    },
    {
      title: "Production",
      key: "production_progress",
      render: (_, record) => {
        const totalQty =
          record.items?.reduce(
            (sum, item) => sum + (Number(item.quantity) || 0),
            0
          ) || 0;
        const producedQty =
          record.items?.reduce(
            (sum, item) => sum + (Number(item.quantity_produced) || 0),
            0
          ) || 0;
        const pct = totalQty > 0 ? Math.round((producedQty / totalQty) * 100) : 0;
        return (
          <Tooltip title={`${producedQty} / ${totalQty} produced`}>
            <Progress
              percent={pct}
              size="small"
              status={pct === 100 ? "success" : "active"}
            />
          </Tooltip>
        );
      },
      width: 150,
    },
    {
      title: "Delivery",
      dataIndex: "expected_delivery",
      key: "expected_delivery",
      sorter: (a, b) => new Date(a.expected_delivery || 0) - new Date(b.expected_delivery || 0),
      render: (date) => {
        if (!date) return "-";
        const deliveryDate = moment(date);
        const isOverdue =
          deliveryDate.isBefore(moment(), "day") &&
          !["delivered", "cancelled"].includes(undefined); // status checked outside
        return (
          <Text type={deliveryDate.isBefore(moment(), "day") ? "danger" : undefined}>
            {deliveryDate.format("DD MMM YYYY")}
          </Text>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      render: (status) => <OrderStatusBadge status={status} />,
    },
    {
      title: "Challan",
      key: "dispatch_receipt_number",
      render: (_, record) =>
        record.dispatch_receipt_number ? (
          <Text>{record.dispatch_receipt_number}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Invoice",
      key: "delivery_receipt_number",
      render: (_, record) =>
        record.delivery_receipt_number ? (
          <Text>{record.delivery_receipt_number}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      defaultSortOrder: "descend",
      render: (date) => (date ? moment(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getActionMenuItems(record)} trigger={["click"]}>
          <Button size="small" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  // Auth check
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  const accessToken = localStorage.getItem("access-token");
  checkTokenExp(accessToken);

  return (
    <>
      <PageTitle title="Back" />
      <Row className="mr-top" justify="space-between" gutter={[0, 30]}>
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={24}
          xl={24}
          className="rounded column-design"
        >
          <Card bordered={false}>
            <div
              className="d-flex justify-content-between"
              style={{ marginBottom: 16 }}
            >
              <Title level={4} className="m-0">
                Orders
              </Title>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/order/new")}
              >
                Create Order
              </Button>
            </div>

            {/* Filters */}
            <div style={{ marginBottom: 16 }}>
              <Space wrap>
                <Input
                  placeholder="Search order #, PO, item, customer, size..."
                  prefix={<SearchOutlined />}
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  style={{ width: 220 }}
                  allowClear
                />
                <Select
                  mode="multiple"
                  placeholder="Statuses"
                  value={filters.status}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                  style={{ width: 280 }}
                  maxTagCount="responsive"
                  allowClear
                >
                  <Option value="confirmed">Confirmed</Option>
                  <Option value="planned">Planned</Option>
                  <Option value="in_production">In Production</Option>
                  <Option value="dispatched">Dispatched</Option>
                  <Option value="delivered">Delivered</Option>
                  <Option value="on_hold">On Hold</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
                <Select
                  placeholder="All Priority"
                  value={filters.priority}
                  onChange={(value) =>
                    setFilters({ ...filters, priority: value })
                  }
                  style={{ width: 130 }}
                  allowClear
                >
                  <Option value="urgent">Urgent</Option>
                  <Option value="high">High</Option>
                  <Option value="normal">Normal</Option>
                  <Option value="low">Low</Option>
                </Select>
                <Select
                  placeholder="All Customers"
                  value={filters.customer_id}
                  onChange={(value) =>
                    setFilters({ ...filters, customer_id: value })
                  }
                  style={{ width: 200 }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {customers.map((c) => (
                    <Option key={c.id || c._id} value={c.id || c._id}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
                <RangePicker onChange={handleDateChange} />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setFilters({
                      status: DEFAULT_ORDER_STATUSES,
                      priority: undefined,
                      customer_id: undefined,
                      search: "",
                      start_date: undefined,
                      end_date: undefined,
                    });
                  }}
                >
                  Clear
                </Button>
              </Space>
            </div>

            {/* Table */}
            <Table
              columns={columns}
              dataSource={orders}
              rowKey="id"
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} orders`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 1300 }}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default OrderList;
